import { AssetNodeType, StockStatus } from './models';

export const UNTITLED_GROUP_LABEL = 'Untitled Group';
export const UNTITLED_ASSET_LABEL = 'Untitled Item';

export const hasUnnamedGroup = (group) => !group?.name;
export const hasUnnamedItem = (item) => !item?.name;

export const getGroupDisplayName = (group) => {
  if (!group) return UNTITLED_GROUP_LABEL;
  if (group.name) return group.name;
  if (group.notes) return group.notes;
  return UNTITLED_GROUP_LABEL;
};

export const getItemDisplayName = (item) => {
  if (!item) return UNTITLED_ASSET_LABEL;
  if (item.name) return item.name;
  if (item.notes) return item.notes;
  return UNTITLED_ASSET_LABEL;
};

const readInventoryCurrent = (inv) => (inv?.onHand != null ? inv.onHand : inv?.quantity ?? null);

const mapInventories = (inventories, source) => {
  if (!inventories || !inventories.length) return [];
  return inventories
    .filter((inv) => !inv.archivedAt)
    .map((inv) => {
      const current = readInventoryCurrent(inv);
      let status = StockStatus.InStock;
      if (current !== null && current !== undefined && inv.preferredStockLevel) {
        if (current === 0) status = StockStatus.OutOfStock;
        else if (current < inv.preferredStockLevel) status = StockStatus.LowStock;
      }
      return {
        id: `inv-${inv.id}`,
        name: inv.name || `Inventory #${inv.id}`,
        status,
        requiredQty: inv.preferredStockLevel != null ? String(inv.preferredStockLevel) : '--',
        currentQty: current,
        raw: inv,
        source: source || null,
      };
    });
};

export const TASK_BUCKET = Object.freeze({
  DailyTask: 'dailyTask',
  PrevMaint: 'prevMaint',
  Project: 'project',
});

const TASK_TYPE_ORDINAL_MAINTENANCE = 0;
const TASK_TYPE_ORDINAL_PROJECT = 1;
const TASK_TYPE_ORDINAL_PREV_MAINT = 4;

export const getTaskBucket = (rawType) => {
  if (rawType == null) return TASK_BUCKET.DailyTask;
  if (typeof rawType === 'number') {
    if (rawType === TASK_TYPE_ORDINAL_PROJECT) return TASK_BUCKET.Project;
    if (rawType === TASK_TYPE_ORDINAL_PREV_MAINT || rawType === TASK_TYPE_ORDINAL_MAINTENANCE) {
      return TASK_BUCKET.PrevMaint;
    }
    return TASK_BUCKET.DailyTask;
  }
  const type = String(rawType).toLowerCase();
  if (
    type === 'preventativemaintenance' ||
    type === 'preventive_maintenance' ||
    type === 'maintenance'
  ) {
    return TASK_BUCKET.PrevMaint;
  }
  if (type === 'project') return TASK_BUCKET.Project;
  return TASK_BUCKET.DailyTask;
};

export const getOccurrenceType = (occurrence) => occurrence?.task?.type ?? occurrence?.type ?? null;

export const isDueToday = (rawDate) => {
  if (!rawDate) return false;
  const d = new Date(rawDate);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const mapTaskOccurrences = (taskOccurrences, source) => {
  if (!taskOccurrences || !taskOccurrences.length) {
    return { dailyTasks: [], preventiveMaintenance: [], projects: [] };
  }
  const dailyTasks = [];
  const preventiveMaintenance = [];
  const projects = [];
  taskOccurrences
    .filter((ti) => !ti.archivedAt)
    .forEach((ti) => {
      const mapped = {
        id: `task-${ti.id}`,
        name: ti.name || ti.task?.name || `Task #${ti.id}`,
        frequency: 'Daily',
        requiresCheck: false,
        requiresPhoto: false,
        instructions: '',
        raw: ti,
        source: source || null,
      };
      const bucket = getTaskBucket(getOccurrenceType(ti));
      if (bucket === TASK_BUCKET.PrevMaint) {
        preventiveMaintenance.push({
          ...mapped,
          interval: '',
          intervalCurrent: 0,
          intervalTotal: 0,
          priority:
            ti.priority === 4
              ? 'critical'
              : ti.priority === 3
              ? 'high'
              : ti.priority === 1
              ? 'low'
              : 'medium',
          completions: 0,
          procedure: '',
          partsNeeded: [],
        });
      } else if (bucket === TASK_BUCKET.Project) {
        projects.push({ ...mapped, status: ti.completedAt ? 'Completed' : 'Active' });
      } else if (!ti.completedAt) {
        // Occurrences are attached to an asset deliberately, so the list shows
        // every open one. Filtering to today's schedule hid anything linked from
        // another day — the row saved, then vanished from the section.
        dailyTasks.push(mapped);
      }
    });
  return { dailyTasks, preventiveMaintenance, projects };
};

const isActive = (row) => !row?.archivedAt;

export const buildGroupNode = (group, depth = 0) => {
  const children = [];
  const groupDisplayName = getGroupDisplayName(group);
  const groupSource = { id: group.id, name: groupDisplayName, kind: 'group' };

  if (group.assetItems) {
    group.assetItems.filter(isActive).forEach((item) => {
      children.push(buildItemNode(item));
    });
  }

  if (group.childGroups) {
    group.childGroups.filter(isActive).forEach((child) => {
      children.push(buildGroupNode(child, depth + 1));
    });
  }

  const taskCount = children.reduce((sum, c) => sum + (c.taskCount || 0), 0);
  const pmCount = children.reduce((sum, c) => sum + (c.pmCount || 0), 0);
  const projectCount = children.reduce((sum, c) => sum + (c.projectCount || 0), 0);
  const inventoryCount = children.reduce((sum, c) => sum + (c.inventoryCount || 0), 0);

  const taskData = mapTaskOccurrences(group.taskOccurrences, groupSource);

  const descendantDailyTasks = [];
  const descendantPrevMaint = [];
  const descendantProjects = [];
  const allInventory = [];
  children.forEach((c) => {
    if (c.allDailyTasks) descendantDailyTasks.push(...c.allDailyTasks);
    if (c.allPreventiveMaintenance) descendantPrevMaint.push(...c.allPreventiveMaintenance);
    if (c.allProjects) descendantProjects.push(...c.allProjects);
    if (c.inventory) allInventory.push(...c.inventory);
  });

  return {
    id: `group-${group.id}`,
    rawId: group.id,
    name: groupDisplayName,
    unnamed: hasUnnamedGroup(group),
    type: AssetNodeType.Group,
    children,
    taskCount: taskCount + taskData.dailyTasks.length,
    pmCount: pmCount + taskData.preventiveMaintenance.length,
    projectCount: projectCount + taskData.projects.length,
    inventoryCount: inventoryCount + allInventory.length,
    dailyTasks: taskData.dailyTasks,
    preventiveMaintenance: taskData.preventiveMaintenance,
    projects: taskData.projects,
    allDailyTasks: [...taskData.dailyTasks, ...descendantDailyTasks],
    allPreventiveMaintenance: [...taskData.preventiveMaintenance, ...descendantPrevMaint],
    allProjects: [...taskData.projects, ...descendantProjects],
    inventory: allInventory,
    raw: group,
  };
};

export const buildItemNode = (item) => {
  const itemDisplayName = getItemDisplayName(item);
  const itemSource = { id: item.id, name: itemDisplayName, kind: 'item' };
  const taskData = mapTaskOccurrences(item.taskOccurrences, itemSource);
  const inventory = mapInventories(item.inventories, itemSource);

  return {
    id: `item-${item.id}`,
    rawId: item.id,
    name: itemDisplayName,
    unnamed: hasUnnamedItem(item),
    type: AssetNodeType.Item,
    children: [],
    taskCount: taskData.dailyTasks.length,
    pmCount: taskData.preventiveMaintenance.length,
    projectCount: taskData.projects.length,
    inventoryCount: inventory.length,
    dailyTasks: taskData.dailyTasks,
    preventiveMaintenance: taskData.preventiveMaintenance,
    projects: taskData.projects,
    allDailyTasks: taskData.dailyTasks,
    allPreventiveMaintenance: taskData.preventiveMaintenance,
    allProjects: taskData.projects,
    inventory,
    raw: item,
  };
};

export const buildTreeFromTemplate = (template) => {
  if (!template) return null;

  const children = [];

  if (template.assetGroups) {
    template.assetGroups.filter(isActive).forEach((group) => {
      children.push(buildGroupNode(group, 0));
    });
  }

  if (template.assetItems) {
    template.assetItems.filter(isActive).forEach((item) => {
      children.push(buildItemNode(item));
    });
  }

  // Tasks and inventory can be linked to the template itself, not only to its
  // groups and items, so the template's own rows are mapped alongside them.
  const templateSource = { id: template.id, name: template.name, kind: 'template' };
  const ownTaskData = mapTaskOccurrences(template.taskOccurrences, templateSource);
  const ownInventory = mapInventories(template.inventories, templateSource);

  const allDailyTasks = [...ownTaskData.dailyTasks];
  const allPrevMaint = [...ownTaskData.preventiveMaintenance];
  const allProjects = [...ownTaskData.projects];
  const allInventory = [...ownInventory];

  children.forEach((c) => {
    if (c.allDailyTasks) allDailyTasks.push(...c.allDailyTasks);
    if (c.allPreventiveMaintenance) allPrevMaint.push(...c.allPreventiveMaintenance);
    if (c.allProjects) allProjects.push(...c.allProjects);
    if (c.inventory) allInventory.push(...c.inventory);
  });

  const taskCount =
    children.reduce((sum, c) => sum + (c.taskCount || 0), 0) + ownTaskData.dailyTasks.length;
  const pmCount =
    children.reduce((sum, c) => sum + (c.pmCount || 0), 0) +
    ownTaskData.preventiveMaintenance.length;
  const projectCount =
    children.reduce((sum, c) => sum + (c.projectCount || 0), 0) + ownTaskData.projects.length;
  const inventoryCount =
    children.reduce((sum, c) => sum + (c.inventoryCount || 0), 0) + ownInventory.length;

  return {
    id: `template-${template.id}`,
    rawId: template.id,
    name: template.name,
    type: AssetNodeType.Template,
    children,
    taskCount,
    pmCount,
    projectCount,
    inventoryCount,
    dailyTasks: allDailyTasks,
    preventiveMaintenance: allPrevMaint,
    projects: allProjects,
    inventory: allInventory,
    raw: template,
  };
};

export const findNodeById = (tree, nodeId) => {
  if (!tree) return null;
  if (tree.id === nodeId) return tree;
  if (!tree.children) return null;
  for (const child of tree.children) {
    const found = findNodeById(child, nodeId);
    if (found) return found;
  }
  return null;
};

export const getBreadcrumbPath = (tree, nodeId, path = []) => {
  if (!tree) return null;
  const currentPath = [...path, { id: tree.id, name: tree.name, type: tree.type }];
  if (tree.id === nodeId) return currentPath;
  if (!tree.children) return null;
  for (const child of tree.children) {
    const result = getBreadcrumbPath(child, nodeId, currentPath);
    if (result) return result;
  }
  return null;
};

export const getAllNodeIds = (tree) => {
  if (!tree) return [];
  const ids = [tree.id];
  if (tree.children) {
    for (const child of tree.children) {
      ids.push(...getAllNodeIds(child));
    }
  }
  return ids;
};

export const filterTree = (tree, searchTerm) => {
  if (!searchTerm) return tree;
  const term = searchTerm.toLowerCase();
  const nameMatches = tree.name.toLowerCase().includes(term);

  if (!tree.children || tree.children.length === 0) {
    return nameMatches ? { ...tree } : null;
  }

  const filteredChildren = tree.children
    .map((child) => filterTree(child, searchTerm))
    .filter(Boolean);

  if (nameMatches || filteredChildren.length > 0) {
    return { ...tree, children: filteredChildren };
  }

  return null;
};

export const getNodeSummary = (node) => {
  return {
    taskCount: node.taskCount || 0,
    pmCount: node.pmCount || 0,
    projectCount: node.projectCount || 0,
    inventoryCount: node.inventoryCount || 0,
  };
};

export const formatNodeCounts = (node) => {
  const parts = [];
  if (node.taskCount) parts.push(`T:${node.taskCount}`);
  if (node.pmCount) parts.push(`PM:${node.pmCount}`);
  if (node.projectCount) parts.push(`P:${node.projectCount}`);
  if (node.inventoryCount) parts.push(`Inv:${node.inventoryCount}`);
  return parts.join(' \u00b7 ');
};
